import { config } from './env';

/**
 * The Google tag, loaded once when a measurement id is configured, and the
 * handful of events worth sending from the app. Nothing here is on the
 * critical path: with no id it is a no-op, and a blocked script is a silent
 * one.
 *
 * `sign_up` is the conversion an ad campaign is buying. It is sent once, the
 * moment an account is created, with the method that created it.
 */

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

let loaded = false;

export function initAnalytics(): void {
  const id = config.gaMeasurementId;
  if (!id || loaded || typeof document === 'undefined') return;
  loaded = true;

  window.dataLayer = window.dataLayer ?? [];
  window.gtag =
    window.gtag ??
    function gtag(...args: unknown[]) {
      window.dataLayer!.push(args);
    };
  window.gtag('js', new Date());
  // send_page_view stays on: the router changes the URL without a load, and
  // GA4's enhanced measurement picks those up as history changes.
  window.gtag('config', id);

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);
}

export function track(event: string, params: Record<string, unknown> = {}): void {
  if (!config.gaMeasurementId) return;
  window.gtag?.('event', event, params);
}

/** An account was created. The conversion. */
export function trackSignUp(method: 'google' | 'password'): void {
  track('sign_up', { method });
}

export function trackLogin(method: 'google' | 'password'): void {
  track('login', { method });
}

/** A team plan, as the thing being bought: how it is billed, how many seats, what each costs. */
export interface SeatOrder {
  interval: 'month' | 'year';
  seats: number;
  perSeatCents: number;
}

function orderParams(order: SeatOrder) {
  return {
    currency: 'USD',
    value: (order.perSeatCents * order.seats) / 100,
    items: [
      {
        item_id: `team-seat-${order.interval}`,
        item_name: `Slate Team seat, ${order.interval === 'year' ? 'yearly' : 'monthly'}`,
        price: order.perSeatCents / 100,
        quantity: order.seats,
      },
    ],
  };
}

/** Off to Stripe Checkout. */
export function trackBeginCheckout(order: SeatOrder): void {
  track('begin_checkout', orderParams(order));
}

/**
 * Back from Stripe with a completed checkout: the revenue conversion. The
 * transaction id is Stripe's checkout session, so a reload of the return page
 * cannot count the same purchase twice.
 */
export function trackPurchase(order: SeatOrder, transactionId: string): void {
  track('purchase', { ...orderParams(order), transaction_id: transactionId });
}
